import { useDropzone } from 'react-dropzone';
import PropTypes from 'prop-types';

import { CustomTypography, TYPOGRAPHY_VARIANT } from 'src/components/CustomTypography';
import { FieldSelect } from 'src/components/FieldSelect';
import { FieldSelectMenuItem } from 'src/components/FieldSelectMenuItem';
import {
  getResourceContentTypeAccept,
  getResourceFileMaxSize,
  RESOURCE_CONTENT_TYPE,
} from 'src/data/performance';
import { pluralize } from 'src/utils/format';

import { useStyles } from './useStyles';

export const SelectResourceFile = ({
  className,
  value,
  onChange,
  onSelectFile,
  disabled,
  documentResources,
  error,
  file,
  isFileUploading,
}) => {
  const styles = useStyles();

  const { getInputProps, getRootProps } = useDropzone({
    noKeyboard: true,
    accept: getResourceContentTypeAccept(RESOURCE_CONTENT_TYPE.document),
    onDrop: onSelectFile,
    maxFiles: 1,
    maxSize: getResourceFileMaxSize(RESOURCE_CONTENT_TYPE.document),
  });

  const renderValue = (valueToRender) => {
    return documentResources.find(({ id }) => id === valueToRender)?.name || file?.name;
  };

  return (
    <div className={className}>
      <FieldSelect
        value={value}
        onChange={onChange}
        disabled={disabled}
        renderValue={renderValue}
        classes={{ menuPaper: styles.paper }}
        error={error}
        placeholder={isFileUploading ? 'Uploading...' : 'Select or upload'}
        MenuProps={{
          keepMounted: true,
        }}
      >
        <FieldSelectMenuItem {...getRootProps()}>
          <CustomTypography
            variant={TYPOGRAPHY_VARIANT.regular14}
            color="primary"
          >
            Upload new file
          </CustomTypography>
          <input {...getInputProps()} />
        </FieldSelectMenuItem>

        {documentResources.map((resource) => (
          <FieldSelectMenuItem
            className={styles.item}
            key={resource.id}
            value={resource.id}
            classes={{ selected: styles.selected }}
          >
            <CustomTypography variant={TYPOGRAPHY_VARIANT.regular14}>
              {resource.name}
            </CustomTypography>

            {resource.file?.pageCount && (
              <CustomTypography
                variant={TYPOGRAPHY_VARIANT.regular14}
                className={styles.pageCount}
              >
                {resource.file.pageCount} {pluralize(resource.file.pageCount, 'page', 'pages')}
              </CustomTypography>
            )}
          </FieldSelectMenuItem>
        ))}
      </FieldSelect>
    </div>
  );
};

SelectResourceFile.propTypes = {
  className: PropTypes.string,
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  onSelectFile: PropTypes.func.isRequired,
  documentResources: PropTypes.arrayOf(PropTypes.shape({})),
  error: PropTypes.string,
  file: PropTypes.shape({
    name: PropTypes.string,
  }),
  isFileUploading: PropTypes.bool,
};

SelectResourceFile.defaultProps = {
  className: '',
  value: undefined,
  disabled: false,
  documentResources: [],
  error: null,
  file: null,
  isFileUploading: false,
};
